import { Zap, ArrowRight, Layers, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/v2/ui'

export type AnalysisMode = 'small' | 'standard' | 'custom'

interface AnalysisModeCardsProps {
  value: AnalysisMode
  onChange: (mode: AnalysisMode) => void
  isAdmin?: boolean
}

const MODES: Array<{
  id: AnalysisMode
  Icon: React.ComponentType<{ className?: string }>
  badgeVariant: 'warning' | 'success' | 'secondary'
}> = [
  { id: 'small',    Icon: Zap,        badgeVariant: 'warning'   },
  { id: 'standard', Icon: ArrowRight, badgeVariant: 'success'   },
  { id: 'custom',   Icon: Layers,     badgeVariant: 'secondary' },
]

const ACTIVE_BORDER: Record<AnalysisMode, string> = {
  small:    'border-amber-500 bg-amber-500/10',
  standard: 'border-primary-500 bg-primary-500/10',
  custom:   'border-primary-500 bg-primary-500/10',
}

const ICON_COLOR: Record<AnalysisMode, string> = {
  small:    'bg-amber-500/15 text-amber-500',
  standard: 'bg-primary-500/15 text-primary-500',
  custom:   'bg-primary-500/15 text-primary-500',
}

export function AnalysisModeCards({ value, onChange, isAdmin }: AnalysisModeCardsProps) {
  const { t } = useTranslation()

  return (
    <div
      className="campaign-creator-modes grid grid-cols-3 gap-2.5"
      role="radiogroup"
      aria-label={t('campaignCreator.modeLabel')}
    >
      {MODES.map(({ id, Icon, badgeVariant }) => {
        const isDisabled = id === 'custom' && !isAdmin

        return (
          <button
            key={id}
            role="radio"
            aria-checked={value === id}
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(id)}
            className={[
              'campaign-creator-mode-card relative rounded-[14px] border-2 p-4 text-left transition-all',
              isDisabled
                ? 'border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800/50 opacity-60 cursor-not-allowed'
                : value === id
                  ? ACTIVE_BORDER[id]
                  : 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600',
            ].join(' ')}
          >
            <span className="absolute top-3 right-3 flex items-center gap-1">
              {isDisabled ? (
                <Badge variant="default" size="sm">
                  {t('campaignCreator.mode.custom.comingSoon')}
                </Badge>
              ) : (
                <Badge variant={badgeVariant} size="sm">
                  {t(`campaignCreator.mode.${id}.badge`)}
                </Badge>
              )}
            </span>
            <div
              className={[
                'mb-2.5 flex h-8 w-8 items-center justify-center rounded-[9px]',
                isDisabled ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400' : ICON_COLOR[id],
              ].join(' ')}
            >
              {isDisabled ? (
                <Lock className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Icon className="h-4 w-4" aria-hidden="true" />
              )}
            </div>
            <div className="text-sm font-bold text-neutral-900 dark:text-white mb-1">
              {t(`campaignCreator.mode.${id}.name`)}
            </div>
            <div className="text-2xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
              {t(`campaignCreator.mode.${id}.desc`)}
            </div>
          </button>
        )
      })}
    </div>
  )
}
